<script>
import { AreaService }  from '@/services/area.service'; 
const debounce = require('lodash/debounce');

/*
 * ---------------------------------------------------------------------------
 * COMMERCIAL IN CONFIDENCE
 *
 * (c) Copyright Quosient Ltd. All Rights Reserved.
 *
 * See LICENSE.txt in the repository root.
 * ---------------------------------------------------------------------------
 */
export default {
    props: {
        google: {
            type: Object,
            required: true,
        },
        map: {
            type: Object,
            required: true,
        },
        layerData: {
            type: Object,
            required: true,
        },
        layerID: {
            type: Number,
            required: true,
        },
        maxOpacity: {
            type: Number,
            required: false,
        },
        isGroup: {
            type: Boolean,
            required: true,
        },
        isMapMoving: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    data() {
        return {
            layer: null
        };
    },
    methods: {
        createOverlay(data) {
            const { ImageMapType } = this.google.maps;

            this.removeOverlay();

            if (!data || !data.mapURL) {
                return;
            }

            let self = this;

            if (!this.layer) {
                console.debug("creating new layer")
                this.layer = new ImageMapType({
                    name: data.name,
                    getTileUrl: (coord, zoom) => {
                        // indicate we have started loading the tiles
                        self.$emit("tiles-loading", true);
                        let url = self.layerData.mapURL
                            .replace("{x}", coord.x)
                            .replace("{y}", coord.y)
                            .replace("{z}", zoom);

                        function loadImage() {
                            // creates the same network call as google maps
                            let img = new Image();
                            img.src = url;

                            img.addEventListener('error', function(){
                                console.debug("tile load error")
                                self.debounceFunction(self.refreshMapTiles)
                            });
                        }
                        loadImage();
                        return url;
                    },
                    tileSize: new this.google.maps.Size(256, 256),
                    minZoom: 1,
                    maxZoom: 20,
                });

                // tilesloaded event fires when all tiles have loaded
                // (will still fire if there are 429 errors)
                this.layer.addListener("tilesloaded", function () {
                    self.$emit("tiles-loading", false);
                });
            }

            this.map.overlayMapTypes.push(this.layer);
        },
        /**
         * removes layer from overlay but doesn't destroy the layer
         */
        removeOverlay() {
            if (this.layer) {
                let index = this.map.overlayMapTypes.indexOf(this.layer);
                if (index > -1) {
                    this.map.overlayMapTypes.removeAt(index);
                }
            }
        },
        getMap() {
            return this.map;
        },
        /**
         * function that forces a refresh of map tiles
         */
        refreshMapTiles(){
            // if it errors it will zoom out and zoom back in to force a refresh of just the broken tiles
            // all the other tiles are kept in cache
            console.debug("changing zoom to force refresh")
            this.map.setZoom(this.map.getZoom()+.000000000000001);
            //this will change the zoom again and load fresh tiles
            // user cannot see as the zoom change is tiny
            this.map.setZoom(Math.round(this.map.getZoom()));
        },
        // function must be in function: debounce - for lodash debounce to work in vue
        debounceFunction: debounce((func)=> {
            console.debug("executing debounce function")
            func()
        }, 1000),
        /**
         * removes layer from map and destroys layer in state
         */
        destroyLayer() {
            if(this.layer) {
                this.removeOverlay()
                this.layer = null;
            }
        },
    },
    mounted() {
        this.clearAreasSubscription = AreaService.clearAreas$.subscribe(
            function () {
                this.removeOverlay();
                this.$emit("tiles-loading", false);
            }.bind(this)
        );
    },
    beforeDestroy() {
        this.clearAreasSubscription.unsubscribe();
    },
    watch: {
        /**
         * checks for whenever layer data changes and creates a new overlay
         * @param {Object} - layer data object
         */
        layerData: {
            immediate: true,
            handler(data) {
                this.createOverlay(data)
            }
        },
        /**
         * watches computed opacity property and updates the layer opacity
         */
        opacity: {
            immediate: true,
            handler(opacity) {
                if (!isNaN(opacity) && this.layer) {
                    this.layer.setOpacity(opacity);
                }
            },
        },
        // handles whether or not to display layer
        displayLayer: {
            immediate: true,
            handler(newDisplayLayer, oldDisplayLayer) {
                // if display layer has changed
                if (newDisplayLayer !== oldDisplayLayer) {
                    // if timeseries
                    if (this.isGroup) {
                        // if the layer should be displayed
                        if (newDisplayLayer) {
                            // check if layer has already been created but not added to the map
                            if(this.map.overlayMapTypes.indexOf(this.layer) === -1) {
                                this.createOverlay(this.layerData);
                            }
                            // set that layers opacity to 1
                            if (this.layerData.opacity === 0) {
                                if (isNaN(this.maxOpacity)) {
                                    this.layerData.opacity = 1;
                                } else {
                                    this.layerData.opacity = this.maxOpacity;
                                }
                            }
                        } else {
                            this.layerData.opacity = 0;
                        }
                    }
                }
            },
        },
        maxOpacity: {
            immediate: true,
            handler(maxOpacity) {
                if (!isNaN(maxOpacity) && this.layerData.displayLayer) {
                    this.layerData.opacity = maxOpacity;
                }
            },
        },
        // if map is moving do not render map layers that are not visible
        // Disabling as it will add layers in different order, causing odd behaviour with other images
        // isMapMoving: {
        //     handler(isMapMoving) {
        //         if(isMapMoving) {
        //             if(!this.displayLayer && this.map.overlayMapTypes.indexOf(this.layer) !== -1){
        //                 console.debug("removing overlay")
        //                 this.removeOverlay()
        //             }
        //         } 
        //     }
        // }
    },
    computed: {
        opacity() {
            return this.layerData.opacity;
        },
        displayLayer() {
            return this.layerData.displayLayer;
        },
    },
    destroyed() {
        this.destroyLayer();
        this.$emit("tiles-loading", false);
    },
    render(h) {
        return h("", "");
    },
};
</script>
